/**
 * Live Tracking — admin dashboard page.
 *
 * Listens on SignalR hub `/hubs/livetracking` group `tracking:admin`
 * for `TrackingSnapshot` events, renders each active trip as a colored
 * marker on a Leaflet map, and provides a filterable / searchable
 * left-side panel for jumping to any trip.
 *
 * Audit 2026-05-20 (LT-07 / UI-02): the in-memory mock fallback that
 * used to kick in when the hub was unreachable has been REMOVED. It
 * silently masked real SignalR / backend failures from operators and
 * gave a false impression of live data. On connection failure we now
 * surface a red "غير متصل" status badge and let automatic-reconnect do
 * its job — operators see exactly what the system sees.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as signalR from '@microsoft/signalr';
import axios from 'axios';
import HelperMixin from '../../Shared/HelperMixin.vue';

// Fix default-icon path issue when bundling via webpack/vue-cli
// (Leaflet looks for marker images at relative URLs by default).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TRIPOLI_CENTER = [32.8872, 13.1913];
const HUB_URL = (typeof process !== 'undefined' && process.env && process.env.VUE_APP_LIVETRACK_HUB_URL)
    || '/hubs/livetracking';

// Trip.Type → color
const TYPE_COLOR = {
    1: '#1e88e5',   // blue   — scheduled / normal
    2: '#43a047',   // green  — shared
    3: '#fb8c00',   // orange — school
    default: '#757575'
};

const TYPE_LABEL = {
    1: 'عادية',
    2: 'مشتركة',
    3: 'مدرسية',
    default: 'أخرى'
};

const COLOR_KEY = {
    1: 'blue',
    2: 'green',
    3: 'orange',
    default: 'gray'
};

function makeDivIcon(type) {
    const color = TYPE_COLOR[type] || TYPE_COLOR.default;
    const html = `
        <div class="lt-marker" style="background:${color};">
            <div class="lt-marker-pulse" style="background:${color};"></div>
            <iconify-icon icon="solar:wheel-bold-duotone" style="color:#fff;font-size:18px;"></iconify-icon>
        </div>
    `;
    return L.divIcon({
        html,
        className: 'lt-marker-wrap',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
    });
}

export default {
    name: 'LiveTracking',
    mixins: [HelperMixin],

    data() {
        return {
            loading: true,
            // 'connected' | 'connecting' | 'disconnected'
            // Audit 2026-05-20 (LT-07): 'mock' removed — mock fallback was
            // hiding real SignalR failures from operators.
            connState: 'disconnected',
            lastUpdate: null,
            trips: [],
            selectedTripId: null,

            // filters
            searchQuery: '',
            activeType: 'all',
            sortBy: 'lastUpdate',

            // reactive map-related UI state
            trailPolyline: null, // mirror for v-if (reactive)
            trailLoading: false,

            typeFilters: [
                { value: 'all',   label: 'الكل',     colorKey: 'all' },
                { value: 1,       label: 'عادية',    colorKey: 'blue' },
                { value: 2,       label: 'مشتركة',   colorKey: 'green' },
                { value: 3,       label: 'مدرسية',   colorKey: 'orange' },
            ],
        };
    },

    computed: {
        selectedTrip() {
            return this.trips.find(t => t.tripId === this.selectedTripId) || null;
        },

        connStateClass() {
            return 'lt-conn-' + this.connState;
        },

        connStateLabel() {
            // Exact labels per audit 2026-05-20 (LT-07): operators must see at
            // a glance whether the live feed is healthy. No more "preview mode"
            // wording — that masked real outages.
            switch (this.connState) {
                case 'connected':    return '🟢 متصل';
                case 'connecting':   return '🟡 جاري الاتصال';
                case 'disconnected': return '🔴 غير متصل';
                default:             return this.connState;
            }
        },

        lastUpdateText() {
            if (!this.lastUpdate) return '';
            const d = new Date(this.lastUpdate);
            return d.toLocaleTimeString('ar-LY', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        },

        filteredTrips() {
            const q = this.searchQuery.trim().toLowerCase();
            let list = this.trips.slice();

            if (this.activeType !== 'all') {
                list = list.filter(t => t.type === this.activeType);
            }
            if (q) {
                list = list.filter(t =>
                    (t.driver    && t.driver.toLowerCase().includes(q))    ||
                    (t.passenger && t.passenger.toLowerCase().includes(q)) ||
                    (t.vehicle   && String(t.vehicle).toLowerCase().includes(q)) ||
                    (t.tripId    && String(t.tripId).includes(q))
                );
            }

            switch (this.sortBy) {
                case 'driver':
                    list.sort((a, b) => (a.driver || '').localeCompare(b.driver || '', 'ar'));
                    break;
                case 'passenger':
                    list.sort((a, b) => (a.passenger || '').localeCompare(b.passenger || '', 'ar'));
                    break;
                case 'status':
                    list.sort((a, b) => (a.status || '').localeCompare(b.status || '', 'ar'));
                    break;
                case 'lastUpdate':
                default:
                    list.sort((a, b) => {
                        const ta = a.lastUpdateUtc ? new Date(a.lastUpdateUtc).getTime() : 0;
                        const tb = b.lastUpdateUtc ? new Date(b.lastUpdateUtc).getTime() : 0;
                        return tb - ta;
                    });
            }
            return list;
        },
    },

    created() {
        // Imperative refs held outside data() so Vue 2 does NOT make them reactive.
        // Vue 2 silently strips data() keys starting with `_` or `$` (see bug audit
        // 2026-05-20). Initialising here makes them plain JS properties on the
        // instance, available from the very first render.
        // mockTimer removed in LT-07 fix (mock fallback deleted).
        this.lMap = null;
        this.lMarkers = {};      // tripId -> L.Marker
        this.hubConn = null;
        this.lTrail = null;
    },

    mounted() {
        this.initMap();
        this.connectHub();
    },

    beforeDestroy() {
        this.disconnectHub();
        if (this.lMap) {
            try { this.lMap.remove(); } catch (_) { /* ignore */ }
            this.lMap = null;
        }
    },

    methods: {
        // ============ Map ============

        initMap() {
            const map = L.map(this.$refs.map, {
                center: TRIPOLI_CENTER,
                zoom: 12,
                zoomControl: true,
                scrollWheelZoom: true,
            });

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            }).addTo(map);

            this.lMap = map;
        },

        // ============ Hub ============

        async connectHub() {
            this.connState = 'connecting';

            // Audit 2026-05-20 (LT-07): the Management dashboard runs under
            // Cookie auth (see Management/Startup.cs AddAuthentication —
            // DefaultAuthenticateScheme = Cookies). `withCredentials: true`
            // sends the auth cookie on the negotiate POST, which is what the
            // hub actually validates. `accessTokenFactory` is provided as a
            // soft fallback so the page also works if a JWT is ever embedded
            // in localStorage["currentUser-client"] (mobile-style auth) —
            // returning an empty string when none is stored means SignalR
            // falls back to the cookie cleanly.
            const conn = new signalR.HubConnectionBuilder()
                .withUrl(HUB_URL, {
                    withCredentials: true,
                    accessTokenFactory: () => this.readStoredJwt() || ''
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
                .withServerTimeout(60000)
                .withKeepAliveInterval(15000)
                .build();

            conn.on('TrackingSnapshot', (snapshot) => {
                // Full snapshot every 5 s — broadcaster sends
                // { GeneratedAtUtc, Count, Trips: [...] }. applySnapshot
                // tolerates array / object / single-trip shapes.
                console.debug('LiveTracking: TrackingSnapshot received', snapshot);
                this.applySnapshot(snapshot, /*isFullSnapshot*/ true);
            });
            conn.on('TripUpdate', (one) => {
                // Optional incremental update for a single trip.
                this.applySnapshot([one], /*isFullSnapshot*/ false);
            });
            conn.on('TripLocationUpdate', (ping) => {
                // Per-ping event from /api/Trips/{id}/Location (admin group).
                // Carries { TripId, Lat, Lng, Speed, Heading, CapturedAt }.
                if (!ping) return;
                const tripId = ping.tripId ?? ping.TripId;
                if (tripId == null) return;
                const idx = this.trips.findIndex(t => t.tripId === tripId);
                if (idx >= 0) {
                    const updated = {
                        ...this.trips[idx],
                        lat: ping.lat ?? ping.Lat ?? this.trips[idx].lat,
                        lng: ping.lng ?? ping.Lng ?? this.trips[idx].lng,
                        speedKmh: ping.speed ?? ping.Speed ?? this.trips[idx].speedKmh,
                        headingDeg: ping.heading ?? ping.Heading ?? ping.HeadingDeg ?? this.trips[idx].headingDeg,
                        lastUpdateUtc: ping.capturedAt ?? ping.CapturedAt ?? new Date().toISOString()
                    };
                    this.$set(this.trips, idx, updated);
                    this.upsertMarker(updated);
                    this.lastUpdate = Date.now();
                }
            });

            conn.onreconnecting(() => { this.connState = 'connecting'; });
            conn.onreconnected(() => {
                this.connState = 'connected';
                console.info('LiveTracking: Connected (reconnected) — rejoining admin channel');
                this.joinAdminChannel();
            });
            conn.onclose((err) => {
                this.connState = 'disconnected';
                if (err) {
                    console.error('LiveTracking: hub closed with error:', err && err.message);
                } else {
                    console.warn('LiveTracking: hub closed');
                }
            });

            try {
                await conn.start();
                this.hubConn = conn;
                this.connState = 'connected';
                console.info('LiveTracking: Connected to', HUB_URL);
                await this.joinAdminChannel();
                await this.loadInitialActiveTrips();
            } catch (err) {
                // Audit 2026-05-20 (LT-07): NO mock fallback. Surface the
                // failure so operators see a red badge and the
                // ops/networking team can be paged. Auto-reconnect on the
                // builder will keep retrying on its configured back-off.
                this.connState = 'disconnected';
                console.error('LiveTracking: hub start FAILED:', err && err.message, err);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Best-effort JWT reader for the SignalR accessTokenFactory.
         * Returns '' (not null) when no token is stored so SignalR uses the
         * cookie path. The dashboard stores the user blob AES-encrypted in
         * localStorage['currentUser-client'] (see Login.js + HelperMixin.vue
         * decrypt key), so we currently do not extract a JWT — the cookie
         * path covers normal admin sessions. The hook is in place so a
         * future build that switches to plain-JWT storage works without
         * touching this file.
         */
        readStoredJwt() {
            try {
                const raw = localStorage.getItem('currentUser-client');
                if (!raw) return '';
                return '';
            } catch (_) {
                return '';
            }
        },

        async joinAdminChannel() {
            // Server method name is JoinAdminChannel (see Common/LiveTrackingHub.cs).
            // Audit 2026-05-20 fixed the prior JoinAdminGroup/SubscribeAdmin mismatch
            // that caused the dashboard to connect but never actually subscribe to
            // the broadcast group, so 5-second snapshots silently disappeared.
            if (!this.hubConn) return;
            try {
                await this.hubConn.invoke('JoinAdminChannel');
            } catch (err) {
                // Surface the failure instead of swallowing it — the dashboard MUST
                // know it is not receiving live updates so the badge can degrade.
                console.error('LiveTracking: JoinAdminChannel failed:', err && err.message);
                this.connState = 'disconnected';
            }
        },

        async loadInitialActiveTrips() {
            try {
                const res = await axios.get('/api/admin/Tracking/ActiveTrips');
                // applySnapshot tolerates both array and {trips:[...]} envelope.
                this.applySnapshot(res.data, /*isFullSnapshot*/ true);
            } catch (err) {
                console.warn('LiveTracking: initial REST snapshot failed:', err && err.message);
            }
        },

        async disconnectHub() {
            if (!this.hubConn) return;
            try { await this.hubConn.stop(); } catch (_) { /* ignore */ }
            this.hubConn = null;
        },

        // ============ Snapshot application ============

        applySnapshot(payload, isFullSnapshot) {
            // Audit 2026-05-20: accept multiple wire shapes —
            //   array of trips            (legacy / TripUpdate)
            //   { Trips: [...] }          (broadcaster pascal-case)
            //   { trips: [...] }          (REST endpoint camel-case)
            //   single trip object        (TripUpdate convenience)
            // Bailing on Array.isArray() previously dropped every real push.
            let snapshot = [];
            if (Array.isArray(payload)) {
                snapshot = payload;
            } else if (payload && Array.isArray(payload.Trips)) {
                snapshot = payload.Trips;
            } else if (payload && Array.isArray(payload.trips)) {
                snapshot = payload.trips;
            } else if (payload && (payload.tripId != null || payload.TripId != null)) {
                snapshot = [payload];
            } else {
                return; // unknown shape
            }
            this.lastUpdate = Date.now();

            const incomingIds = new Set();
            snapshot.forEach(raw => {
                const trip = this.normalizeTrip(raw);
                // Only drop trips with NO coordinate at all. A present-but-invalid
                // fix (0,0 / out-of-Libya / transient glitch) must still count as
                // "seen" here, otherwise the full-snapshot reaper below would delete
                // a still-active trip on one bad ping. The out-of-bounds guard lives
                // in upsertMarker, which keeps the last good marker position instead.
                if (!trip || trip.lat == null || trip.lng == null) return;
                incomingIds.add(trip.tripId);

                const idx = this.trips.findIndex(t => t.tripId === trip.tripId);
                if (idx === -1) {
                    this.trips.push(trip);
                } else {
                    this.$set(this.trips, idx, { ...this.trips[idx], ...trip });
                }
                this.upsertMarker(trip);
            });

            // Reap stale markers only on a full snapshot. Incremental updates
            // (TripUpdate / TripLocationUpdate) must NOT trigger removal.
            if (isFullSnapshot) {
                const toRemove = this.trips
                    .filter(t => !incomingIds.has(t.tripId))
                    .map(t => t.tripId);
                toRemove.forEach(id => this.removeTrip(id));
            }
        },

        normalizeTrip(raw) {
            if (!raw) return null;
            // Audit 2026-05-20 (B4): server may legitimately send `type` as a
            // numeric short (preferred wire contract) OR as a string label like
            // "Regular"|"Shared"|"Schools"|"Parent" (legacy). Coerce both to the
            // numeric domain so filter chips, colors, and labels work.
            const rawType = raw.type ?? raw.Type ?? 1;
            let typeNum;
            if (typeof rawType === 'number') {
                typeNum = rawType;
            } else if (typeof rawType === 'string') {
                const labelMap = { Regular: 1, Shared: 2, Schools: 3, School: 3, Parent: 4 };
                typeNum = labelMap[rawType] ?? Number(rawType);
                if (Number.isNaN(typeNum)) typeNum = 1;
            } else {
                typeNum = Number(rawType) || 1;
            }
            return {
                tripId:        raw.tripId        ?? raw.TripId        ?? raw.id,
                type:          typeNum,
                driver:        raw.driver        ?? raw.Driver         ?? raw.driverName    ?? raw.DriverName ?? '',
                passenger:     raw.passenger     ?? raw.Passenger      ?? raw.passengerName ?? '',
                vehicle:       raw.vehicle       ?? raw.Vehicle        ?? raw.plate         ?? raw.VehiclePlate ?? '',
                status:        raw.status        ?? raw.Status         ?? '',
                lat:           raw.lat           ?? raw.Lat            ?? raw.latitude,
                lng:           raw.lng           ?? raw.Lng            ?? raw.longitude,
                speedKmh:      raw.speedKmh      ?? raw.SpeedKmh       ?? raw.speed         ?? null,
                // headingDeg captured for future arrow-marker orientation; the
                // server (LiveTrackingBroadcaster) sends it via HeadingDeg.
                headingDeg:    raw.headingDeg    ?? raw.HeadingDeg     ?? raw.heading       ?? raw.Heading       ?? null,
                lastUpdateUtc: raw.lastUpdateUtc ?? raw.LastUpdateUtc  ?? raw.timestamp     ?? new Date().toISOString(),
                eta:           raw.eta           ?? raw.Eta            ?? null,
                startTime:     raw.startTime     ?? raw.StartTime      ?? null,
                hasCamera:     !!(raw.hasCamera ?? raw.HasCamera),
                cameraUrl:     raw.cameraUrl     ?? raw.CameraUrl      ?? null,
            };
        },

        isValidLatLng(lat, lng) {
            const la = Number(lat), ln = Number(lng);
            // Reject null-island / out-of-region fixes. Libya bounding box
            // (generous): lat 19..34, lng 9..26. Prevents a 0,0 or swapped
            // coordinate from rendering a marker "in the sea".
            return isFinite(la) && isFinite(ln) && !(la === 0 && ln === 0)
                && la >= 19 && la <= 34 && ln >= 9 && ln <= 26;
        },

        upsertMarker(trip) {
            if (!this.lMap) return;
            // Never plot an out-of-Libya / 0,0 coordinate (e.g. a stale or bad
            // ping) — keep the last known good position instead of jumping to sea.
            if (!this.isValidLatLng(trip.lat, trip.lng)) return;
            const existing = this.lMarkers[trip.tripId];
            const latlng = [Number(trip.lat), Number(trip.lng)];

            if (existing) {
                existing.setLatLng(latlng);
            } else {
                const marker = L.marker(latlng, { icon: makeDivIcon(trip.type) })
                    .addTo(this.lMap)
                    .on('click', () => this.selectTrip(trip.tripId));
                marker.bindTooltip(this.tooltipHtml(trip), {
                    direction: 'top',
                    offset: [0, -10],
                    className: 'lt-marker-tooltip'
                });
                this.lMarkers[trip.tripId] = marker;
            }
        },

        removeTrip(tripId) {
            const idx = this.trips.findIndex(t => t.tripId === tripId);
            if (idx >= 0) this.trips.splice(idx, 1);
            const m = this.lMarkers[tripId];
            if (m && this.lMap) {
                this.lMap.removeLayer(m);
            }
            delete this.lMarkers[tripId];
            if (this.selectedTripId === tripId) {
                this.closeDetail();
            }
        },

        tooltipHtml(trip) {
            const label = TYPE_LABEL[trip.type] || TYPE_LABEL.default;
            return `<div class="lt-tt"><b>#${trip.tripId}</b> · ${label}<br>${trip.driver || ''}</div>`;
        },

        // ============ Selection ============

        selectTrip(tripId) {
            this.selectedTripId = tripId;
            const trip = this.trips.find(t => t.tripId === tripId);
            if (trip && this.lMap) {
                this.lMap.flyTo([Number(trip.lat), Number(trip.lng)], 15, { duration: 0.6 });
                const marker = this.lMarkers[tripId];
                if (marker) marker.openTooltip();
            }
        },

        closeDetail() {
            this.selectedTripId = null;
            this.clearTrail();
        },

        // ============ Trail (route polyline) ============

        async showTrail(tripId) {
            // Audit 2026-05-20 (B7): server returns { trip, count, trail: [...] }
            // — the previous reader looked for `res.data.points` which never
            // existed → trail always empty → no polyline. Also the synthetic
            // fallback was removed because drawing fake GPS data is misleading
            // to operators (it looks like real movement).
            if (!tripId) return;
            this.trailLoading = true;
            try {
                const res = await axios.get(`/api/admin/Tracking/${tripId}/Trail`);
                let points;
                if (Array.isArray(res.data)) {
                    points = res.data;
                } else if (res.data && Array.isArray(res.data.trail)) {
                    points = res.data.trail;
                } else if (res.data && Array.isArray(res.data.points)) {
                    points = res.data.points; // legacy shape
                } else {
                    points = [];
                }
                this.drawTrail(points);
            } catch (err) {
                console.error('LiveTracking: trail fetch failed:', err && err.message);
                this.clearTrail();
            } finally {
                this.trailLoading = false;
            }
        },

        drawTrail(points) {
            this.clearTrail();
            if (!points || points.length === 0 || !this.lMap) return;
            const latlngs = points
                .map(p => [Number(p.lat ?? p.Lat ?? p.latitude), Number(p.lng ?? p.Lng ?? p.longitude)])
                .filter(c => !Number.isNaN(c[0]) && !Number.isNaN(c[1]));
            if (latlngs.length === 0) return;

            this.lTrail = L.polyline(latlngs, {
                color: '#5d87ff',
                weight: 5,
                opacity: 0.85,
                dashArray: '6 6'
            }).addTo(this.lMap);

            this.trailPolyline = true;
            this.lMap.fitBounds(this.lTrail.getBounds(), { padding: [40, 40] });
        },

        clearTrail() {
            if (this.lTrail && this.lMap) {
                this.lMap.removeLayer(this.lTrail);
            }
            this.lTrail = null;
            this.trailPolyline = false;
        },

        // ============ Camera ============

        openCamera(trip) {
            if (!trip || !trip.hasCamera) return;
            if (trip.cameraUrl) {
                window.open(trip.cameraUrl, '_blank', 'noopener,noreferrer');
            } else {
                window.open(`/Trips?focus=${trip.tripId}&action=camera`, '_blank', 'noopener,noreferrer');
            }
        },

        // ============ UI helpers ============

        typeColorKey(type) { return COLOR_KEY[type] || COLOR_KEY.default; },
        typeLabel(type)    { return TYPE_LABEL[type] || TYPE_LABEL.default; },
        countByType(type)  { return this.trips.filter(t => t.type === type).length; },

        formatRelative(iso) {
            if (!iso) return '—';
            const now = Date.now();
            const t = new Date(iso).getTime();
            if (Number.isNaN(t)) return '—';
            const sec = Math.max(0, Math.round((now - t) / 1000));
            if (sec < 60)   return `قبل ${sec} ث`;
            if (sec < 3600) return `قبل ${Math.round(sec / 60)} د`;
            return `قبل ${Math.round(sec / 3600)} س`;
        },

        formatDate(iso) {
            if (!iso) return '—';
            try {
                return new Date(iso).toLocaleString('ar-LY', {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit'
                });
            } catch (_) {
                return iso;
            }
        }

        // ============ NOTE ============
        // The in-memory mock data generator was removed in audit
        // 2026-05-20 (LT-07 / UI-02). It used to silently kick in when the
        // SignalR hub was unreachable, presenting fake trips to operators
        // as though they were live — masking real backend outages. The
        // dashboard now shows an explicit "🔴 غير متصل" badge instead and
        // the SignalR client's automatic-reconnect handles transient drops.
    }
};
